/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useEffect, useState } from 'react';

import { useRetracked } from 'js/lib/retracked';
import { get } from 'js/lib/user';

import { TextField } from '@coursera/cds-core';

import { SendIcon } from 'bundles/ai-course-coach/assets/mui';
import { CoachIconButton } from 'bundles/ai-course-coach/components/common';

import _t from 'i18n!nls/academic-integrity';

const styles = {
  inputContainer: css`
    padding: var(--cds-spacing-400) var(--cds-spacing-400) 0 var(--cds-spacing-400);
  `,
  input: css`
    width: 100%;

    > label {
      margin-bottom: 0;

      /* visually hidden but required for a11y */
      display: none;
    }

    .cds-text-field-validationIcon {
      display: none;
    }

    /* CDS overrides for Coach */
    .cds-text-field-root {
      border-radius: var(--cds-spacing-100);

      /* remove border on the input field */
      .cds-input-notchedOutline {
        border: none;
      }

      .cds-inputAdornment-filled {
        padding: 0;
        background-color: transparent;
      }

      &.cds-input-focused::before {
        border-radius: var(--cds-spacing-100);
      }

      &:hover {
        .cds-input-notchedOutline {
          border: 1px solid var(--cds-color-blue-600);
        }
      }
    }
  `,
  sendIconContainer: css`
    position: absolute;
    top: var(--cds-spacing-50);
    right: var(--cds-spacing-150);
  `,
};

type IntegrityCheckChatInputProps = {
  onSubmit: (answer: string) => Promise<void>;
  isLoading: boolean;
  retriableAnswer?: string;
  assignmentId?: string;
  vivaExamSessionId?: string | null;
};

const IntegrityCheckChatInput = ({
  onSubmit,
  isLoading,
  retriableAnswer,
  assignmentId,
  vivaExamSessionId,
}: IntegrityCheckChatInputProps) => {
  const [inputValue, setInputValue] = useState('');
  const [inputError, setInputError] = useState(false);

  const track = useRetracked();

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
    if (inputError && event.target.value.trim() !== '') {
      setInputError(false);
    }
  };

  const handleSend = () => {
    if (inputValue.trim() !== '') {
      onSubmit(inputValue);
      setInputValue('');
    } else {
      setInputError(true);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const inputElementProps = {
    onKeyPress: handleKeyPress,
  };

  const isButtonDisabled = inputValue.trim() === '' || isLoading;

  // Set input value to retriableAnswer if it exists
  useEffect(() => {
    if (retriableAnswer !== undefined) {
      setInputValue(retriableAnswer);
    }
  }, [retriableAnswer]);

  return (
    <div
      css={styles.inputContainer}
      onPaste={(event) => {
        const clipboardTextLength = event.clipboardData?.getData('text').length;
        track({
          trackingName: 'viva_exam_pasting_detection',
          action: 'paste',
          trackingData: {
            userId: get().id,
            clipboardTextLength,
            // eslint-disable-next-line camelcase
            assignment_id: assignmentId,
            vivaExamSessionId,
          },
        });
      }}
    >
      <TextField
        css={styles.input}
        value={inputValue}
        disabled={isLoading}
        inputProps={inputElementProps}
        onChange={handleInputChange}
        label={_t('Answer Text field')}
        placeholder={_t('Share your response here')}
        multiline
        validationStatus={inputError ? 'error' : undefined}
        validationLabel={inputError ? _t('Response cannot be empty.') : undefined}
        minRows={4}
        suffix={
          <div css={styles.sendIconContainer}>
            <CoachIconButton
              disabled={isButtonDisabled}
              tooltip=""
              variant="ghost"
              onClick={handleSend}
              aria-label={_t('Send message')}
              data-testid="viva-exam-send-button"
              icon={<SendIcon />}
            />
          </div>
        }
      />
    </div>
  );
};

export default IntegrityCheckChatInput;
